import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { 
  clearAllData,
} from '../utils/localStorage';
import {
  isPushSupported,
  getPermissionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
  REMINDER_PRESETS,
  saveReminderSettings,
  getReminderSettings
} from '../utils/pushNotifications';
import { 
  Settings as SettingsIcon, 
  Bell, 
  Trash2,
  RefreshCw,
  Heart,
  Info,
  CheckCircle,
  XCircle,
  Loader2,
  BellRing,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import TaskEditor from './TaskEditor';

const Settings = () => {
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [reminderSettings, setReminderSettings] = useState(getReminderSettings());
  
  useEffect(() => {
    // Check push support and status on mount
    const checkPushStatus = async () => {
      const supported = isPushSupported();
      setPushSupported(supported);
      
      if (supported) {
        setPermissionStatus(getPermissionStatus());
        const subscribed = await isSubscribed();
        setPushEnabled(subscribed);
      }
    };
    
    checkPushStatus();
  }, []);
  
  const handlePushToggle = async (checked) => {
    setPushLoading(true);
    
    try {
      if (checked) {
        await subscribeToPush();
        setPushEnabled(true);
        setPermissionStatus('granted');
        toast.success('Push notifications enabled! 🔔');
      } else {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      }
    } catch (error) {
      console.error('Push toggle error:', error);
      
      if (error.message.includes('denied')) {
        setPermissionStatus('denied');
        toast.error('Notification permission was denied. Please enable it in your browser settings.');
      } else {
        toast.error('Failed to update notification settings');
      }
    } finally {
      setPushLoading(false);
    }
  };
  
  const handleReminderToggle = (reminderId, checked) => {
    const newSettings = {
      ...reminderSettings,
      reminders: {
        ...reminderSettings.reminders,
        [reminderId]: checked
      }
    };
    setReminderSettings(newSettings);
    saveReminderSettings(newSettings);
    
    if (checked) {
      toast.success('Reminder enabled');
    }
  };
  
  const handleReset = () => {
    clearAllData();
    toast.success('All data cleared. Starting fresh.');
    window.location.href = '/';
  };
  
  return (
    <div className="space-y-6 relative" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            Settings
          </h2>
          <p className="text-ink-500 font-accent italic">
            Customize your experience
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-brown/10 flex items-center justify-center border border-brown/20">
          <SettingsIcon className="w-6 h-6 text-brown" />
        </div>
      </div>
      
      {/* Push Notifications */}
      <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="w-5 h-5 text-brown" />
          <h3 className="font-serif font-medium text-ink-900">Push Notifications</h3>
        </div>
        
        {!pushSupported ? (
          <div className="p-3 bg-warmth-sand rounded-lg text-sm text-ink-700">
            <Info className="w-4 h-4 inline mr-2" />
            Push notifications are not supported in this browser. Try using Chrome, Firefox, or Safari.
          </div>
        ) : permissionStatus === 'denied' ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <XCircle className="w-4 h-4 inline mr-2" />
            Notifications are blocked. Please enable them in your browser settings to receive gentle reminders.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="push-enabled" className="text-ink-900">
                  Enable Notifications
                </Label>
                <p className="text-xs text-ink-500 mt-1 font-accent italic">
                  Receive gentle reminders throughout the day
                </p>
              </div>
              <div className="flex items-center gap-2">
                {pushLoading && (
                  <Loader2 className="w-4 h-4 animate-spin text-brown" />
                )}
                <Switch
                  id="push-enabled"
                  data-testid="settings-push-toggle"
                  checked={pushEnabled}
                  onCheckedChange={handlePushToggle}
                  disabled={pushLoading}
                />
              </div>
            </div>
            
            {pushEnabled && (
              <div className="p-3 bg-sage/10 border border-sage/20 rounded-lg text-sm text-ink-700 animate-fade-in flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-sage" />
                Notifications are enabled! You'll receive gentle reminders.
              </div>
            )}
          </div>
        )}
      </Card>
      
      {/* Gentle Reminders */}
      {pushEnabled && (
        <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <BellRing className="w-5 h-5 text-sage" />
            <h3 className="font-serif font-medium text-ink-900">Gentle Reminders</h3>
          </div>
          
          <p className="text-xs text-ink-500 mb-4 font-accent italic">
            Choose which reminders you'd like to receive. These are soft nudges, not demands.
          </p>
          
          <div className="space-y-3">
            {REMINDER_PRESETS.map((reminder) => (
              <div 
                key={reminder.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-stone-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink-900 text-sm">
                      {reminder.label}
                    </span>
                    <span className="text-xs text-ink-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {reminder.time}
                    </span>
                  </div>
                  <p className="text-xs text-ink-500 mt-1 font-accent italic">
                    {reminder.message}
                  </p>
                </div>
                <Switch
                  checked={reminderSettings.reminders[reminder.id] || false}
                  onCheckedChange={(checked) => handleReminderToggle(reminder.id, checked)}
                  data-testid={`reminder-toggle-${reminder.id}`}
                />
              </div>
            ))}
          </div>
          
          <div className="mt-4 p-3 bg-warmth-sand rounded-lg text-xs text-ink-600">
            <Info className="w-4 h-4 inline mr-2" />
            Note: Reminders will be sent around the scheduled times. For them to work, keep the app installed on your device.
          </div>
        </Card>
      )}
      
      {/* Task Customization */}
      <TaskEditor />
      
      {/* About */}
      <Card className="p-6 bg-warmth-sand border border-stone-300 relative overflow-hidden">
        <div className="flex items-center gap-3 mb-4">
          <Heart className="w-5 h-5 text-warmth-rust" />
          <h3 className="font-serif font-medium text-ink-900">About The Calm Keeper Compass</h3>
        </div>
        
        <p className="text-sm text-ink-700 leading-relaxed mb-4 font-accent">
          Built for stay-at-home moms who are tired of ending the day feeling behind, 
          even when they were busy all day. This isn't about doing more — it's about 
          deciding what's enough.
        </p>
        
        <div className="text-xs text-ink-500 font-handwritten text-lg">
          <p>Version 1.0.0</p>
          <p>Made with care and understanding 💝</p>
        </div>
      </Card>
      
      {/* Reset data */}
      <Card className="p-6 bg-paper-light shadow-lifted border border-destructive/20">
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="w-5 h-5 text-destructive" />
          <h3 className="font-serif font-medium text-ink-900">Data Management</h3>
        </div>
        
        <p className="text-sm text-ink-500 mb-4 font-accent italic">
          Clear all your data and start fresh. This will remove all tasks, 
          brain dumps, reflections, and settings. This action cannot be undone.
        </p>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              data-testid="settings-reset-btn"
              variant="outline"
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset All Data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif">Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all your data including tasks, 
                brain dumps, reflections, and settings. You'll go through 
                onboarding again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="settings-reset-confirm"
                onClick={handleReset}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Yes, reset everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
};

export default Settings;
